import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Box, Column, Divider, Spinner, TableListHeader, TableListProvider, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { getInvoiceStatusFilterOptions } from '~/components/InvoicesTable/InvoicesTable.utils';
import { FilterType as InvoiceFilterType, getTableFilterConfig, getTableFilterLabels, invoiceFilterTextsPath } from '~/components/InvoicesTable/invoicesTableFilters';
import TableFilters from '~/components/TableFilters/TableFilters';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import styles from './ClientInvoicesListView.module.scss';
import { applyClientInvoicesFilters, applyClientInvoicesSort, DEFAULT_SORT, distinctAgreementCount, EMPTY_FILTERS, getClientInvoicesTableDefs, parseDateRangeFilter } from './ClientInvoicesListView.utils';
import { ClientInvoicesListViewRow } from './ClientInvoicesListViewRow';
const clientInvoicesListViewTextsPath = 'pages.clientDetail.tabContent.issuedInvoices';
const ACTIVE_FILTERS = [
    {
        label: '',
        filters: [
            InvoiceFilterType.InvoiceStatus,
            InvoiceFilterType.Date
        ]
    }
];
export const GetClientInvoicesListViewTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientInvoicesListView(param) {
    let { invoices, isLoading, customerId, testId = '' } = param;
    const { ClientInvoicesListViewTestIds: TID } = useMemo(()=>GetClientInvoicesListViewTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(clientInvoicesListViewTextsPath);
    const { t: invoiceTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const isMobileDevice = useIsMobileForHomePage();
    const { urlState, changeUrlState } = useStateFromUrl({
        status: {
            defaultValue: '',
            applyDefaultOnlyOnMount: true
        }
    });
    const initialStatusFromUrl = useMemo(()=>{
        const raw = urlState.status;
        if (!raw) return [];
        return raw.split(',').map((s)=>s.trim()).filter((s)=>Object.values(InvoiceDisplayStatusV2).includes(s));
    }, [
        urlState.status
    ]);
    const [search, setSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(()=>initialStatusFromUrl.length > 0 ? {
            [InvoiceFilterType.InvoiceStatus]: initialStatusFromUrl
        } : {});
    const [sort, setSort] = useState(DEFAULT_SORT);
    const seedStripped = useRef(false);
    useEffect(()=>{
        if (seedStripped.current) return;
        seedStripped.current = true;
        if (urlState.status) changeUrlState({
            status: ''
        });
    }, [
        urlState.status,
        changeUrlState
    ]);
    const showAgreementName = useMemo(()=>distinctAgreementCount(invoices) > 1, [
        invoices
    ]);
    const { columnDefs, headerItems } = useMemo(()=>getClientInvoicesTableDefs({
            showAgreementName,
            texts: {
                invoiceId: texts.columns.invoiceId(),
                agreementName: texts.columns.agreementName(),
                invoiceStatus: texts.columns.invoiceStatus(),
                additionalInfo: texts.columns.additionalInfo(),
                issueDate: texts.columns.issueDate(),
                dueDate: texts.columns.dueDate(),
                total: texts.columns.total()
            }
        }), [
        showAgreementName,
        texts
    ]);
    const sortIds = useMemo(()=>headerItems.map((h)=>h.id), [
        headerItems
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(invoiceTexts.filterLabels), [
        invoiceTexts
    ]);
    const statusFilterOptions = useMemo(()=>{
        const counters = Object.values(InvoiceDisplayStatusV2).reduce((acc, status)=>_object_spread_props(_object_spread({}, acc), {
                [status]: invoices.filter((inv)=>inv.displayStatusV2 === status).length
            }), {});
        return getInvoiceStatusFilterOptions({
            counters,
            texts: invoiceTexts.invoiceDisplayStatusLabels
        });
    }, [
        invoices,
        invoiceTexts
    ]);
    const filterConfig = useMemo(()=>getTableFilterConfig({
            invoiceStatusFilterOptions: statusFilterOptions,
            issuesPreventingPaymentFilterOptions: [],
            processedByFilterOptions: [],
            qboStatusFilterOptions: [],
            xeroStatusFilterOptions: []
        }), [
        statusFilterOptions
    ]);
    const visible = useMemo(()=>{
        var _selectedFilters_InvoiceFilterType_InvoiceStatus;
        const statuses = (_selectedFilters_InvoiceFilterType_InvoiceStatus = selectedFilters[InvoiceFilterType.InvoiceStatus]) !== null && _selectedFilters_InvoiceFilterType_InvoiceStatus !== void 0 ? _selectedFilters_InvoiceFilterType_InvoiceStatus : [];
        const { since, until } = parseDateRangeFilter(selectedFilters[InvoiceFilterType.Date]);
        const filtered = applyClientInvoicesFilters(invoices, _object_spread_props(_object_spread({}, EMPTY_FILTERS), {
            search,
            statuses,
            issuedOnSince: since,
            issuedOnUntil: until
        }));
        return applyClientInvoicesSort(filtered, sort, sortIds);
    }, [
        invoices,
        search,
        sort,
        sortIds,
        selectedFilters
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Spinner, {
                testId: TID.Loader()
            })
        });
    }
    if (invoices.length === 0) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.Empty(),
                skin: "secondary",
                children: texts.emptyMessage()
            })
        });
    }
    return /*#__PURE__*/ _jsx(Column, {
        testId: TID.Root(),
        gap: 16,
        className: styles.root,
        children: /*#__PURE__*/ _jsxs(TableListProvider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: sort,
            onSortChange: setSort,
            variant: "minimal",
            children: [
                /*#__PURE__*/ _jsx(TableFilters, {
                    testIdPrefix: TID.Root(),
                    filterLabels: filterLabels,
                    filterConfig: filterConfig,
                    selectedFilters: selectedFilters,
                    setSelectedFilters: (next)=>{
                        setSelectedFilters(next);
                        if (urlState.status) changeUrlState({
                            status: ''
                        });
                    },
                    filterGroups: ACTIVE_FILTERS,
                    onRefresh: ()=>undefined,
                    onClear: ()=>{
                        setSearch('');
                        setSelectedFilters({});
                        if (urlState.status) changeUrlState({
                            status: ''
                        });
                    },
                    isClearDisabled: isMobileDevice ? search === '' && Object.keys(selectedFilters).length === 0 : false,
                    modernClearButton: true,
                    search: {
                        bi: {
                            name: 'client_invoices_search'
                        },
                        value: search,
                        placeholder: texts.searchPlaceholder(),
                        testId: TID.Search(),
                        onChange: (event)=>setSearch(event.target.value),
                        onClear: ()=>setSearch('')
                    }
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    className: cn(styles.tableBody, {
                        [styles.tableBodyDesktop]: !isMobileDevice
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(TableListHeader, {
                            testId: TID.Header(),
                            items: headerItems
                        }),
                        visible.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                testId: TID.NoResults(),
                                skin: "secondary",
                                children: texts.noResults()
                            })
                        }) : visible.map((invoice, idx)=>/*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(ClientInvoicesListViewRow, {
                                        invoice: invoice,
                                        customerId: customerId,
                                        showAgreementName: showAgreementName,
                                        testId: TID.Row(invoice.id),
                                        compact: isMobileDevice,
                                        disableMarginBottom: true
                                    }),
                                    !isMobileDevice && idx < visible.length - 1 && /*#__PURE__*/ _jsx(Divider, {
                                        margin: 0
                                    })
                                ]
                            }, invoice.id))
                    ]
                })
            ]
        })
    });
}
